<template lang="pug">
	.dashboard
		.flex-col.space
			.flex-row.justify.wrap.nav-header-wrapper
				.flex-col.nav-header.space
					.flex-row.space.align-center
						div.nowrap {{$tCaption('Creative dashboard')}}
						Loader(v-if="!isDataLoaded && isLoading", text="")
			.flex-row
				.flex-col.justify
					.flex-col.dashboard-items.space-large
						.flex-row.space-large.align-end.lr-padding
							.campaigns-select-box(v-if="!campaignId")
								.flex-col.field
									.font-mini.upper.grey {{$tCaption('Campaign')}}
									Dropdown(
										:getTotal="(data) => data.TotalNumberOfRecords", :getItems="(data) => data.Results", :getData="$Data.getExternalCampaigns",
										:columns="[{ dataField: 'Name', caption: $tCaption('Campaign') }]",
										displayExpr="Name",
										valueExpr="Oid",
										:placeholder="$tCaption('Choose campaign')",
										:selectedItems="logsOptions.campaignId",
										:showColumnHeaders="false",
										:filtersVisible="false",
										:searchVisible="true",
										:multiple="false",
										@select-data="item => selectCampaign(item && item.Oid)"
									)
							.flex-col
								.font-small.upper.grey {{$tCaption('Creatives')}}
								Dropdown(
									ref="creatives",
									:getTotal="(data) => data.TotalNumberOfRecords", :getItems="(data) => data.Results", :getData="getCreatives",
									:columns="creativeColumns",
									displayExpr="Name",
									valueExpr="Oid",
									:placeholder="$tCaption('Choose creatives')",
									v-model="creativeIds",
									:showColumnHeaders="true",
									:filtersVisible="true",
									:searchVisible="true",
									:multiple="true",
									:dropDownOptions="dropDownOptions",
									:reloadOnOpen="true",
									@select-data="onCreativesSelect"
								)
							.flex-col(:class="{ 'disabled-controls': !creativeIds || !creativeIds.length }")
								.font-small.upper.grey {{$tCaption('Locations')}}
								Dropdown(
									:getTotal="(data) => data.TotalNumberOfRecords", :getItems="(data) => data.Results", :getData="getTargets",
									:columns="targetColumns",
									displayExpr="Name",
									valueExpr="Oid",
									:placeholder="$tCaption('Choose locations')",
									v-model="targetIds",
									:showColumnHeaders="false",
									:filtersVisible="true",
									:searchVisible="true",
									:multiple="true",
									:dropDownOptions="dropDownOptions",
									:reloadOnOpen="true"
								)
							.flex-row.space-large.date-box-filter
								.flex-col.field
									.font-mini.upper.grey {{$tCaption('Start date')}}
									DxDateBox(
										ref="dateStartDateBox",
										:placeholder="'Choose date'",
										:value="rangeStartValue",
										:dropDownOptions="dateStartDropDownOptions",
										:show-clear-button="true"
										:disabled="isLoading"
										display-format="yyyy-MM-dd"
										:width="150"
										@value-changed="v => selectDate(v.value, 'dateStart')"
									)
								.flex-col.field
									.font-mini.upper.grey {{$tCaption('End date')}}
									DxDateBox(
										ref="dateEndDateBox",
										:placeholder="'Choose date'",
										:value="rangeEndValue",
										:dropDownOptions="dateEndDropDownOptions",
										:show-clear-button="true"
										:disabled="isLoading"
										display-format="yyyy-MM-dd"
										:width="150"
										@value-changed="v => selectDate(v.value, 'dateEnd')"
									)
							.flex-col.field
								.font-mini.upper.grey {{$tCaption('Period')}}
								DxSelectBox(:data-source="filters", :height="36", valueExpr="id", displayExpr="text", :value="currentFilter", @item-click="v => changeDateRadioFilter(v.itemData.id)", :disabled="isLoading")
						.flex-row(v-if="isDataLoaded")
							.flex-col.flex-1.space
								RangeSelector(
									:range-data-source="rangeDataSource"
									:range-value="rangeValue"
									:start-value="new Date(rangeStartValue)"
									:end-value="new Date(rangeEndValue)"
									:series="rangeSeries"
									range-source-field="date"
									:is-loading="isLoading"
									@selectRange="selectRangeValue"
								)
								.flex-row.space
									.flex-col.flex-3
										.map-values
											.dash-loader(v-if="loading.mapValues")
												.loader-icon
													i.fas.fa-circle-notch.fa-spin
											.map-holder
												.map
													LMap(
														ref="map"
														:zoom="zoom"
														:max-zoom="18",
														:center="mapCenter"
														:disabled="isLoading"
														@update:center="updateMapCenter"
														@update:zoom="updateMapZoom"
													)
														LTileLayer(
															ref="mapTileLayer"
															:url="baseMapUrl",
														)

														VMarkerCluster(
															v-if="!isLoading"
															ref="markerCluster",
															:options="markerClusterOptions"
															@clusterclick="clusterClick"
														)
															LMarker(
																v-for="({lat, lng, value}, i) in markers"
																v-if="value !== null"
																:ref="`vMaker${i}`"
																:key="`vMaker${i}`"
																:lat-lng="{lat, lng}"
																:title="value"
																@click="markerClick"
															)

									.flex-col.flex-5.space
										.flex-row.space
											.flex-col.justify-center.align-center.flex-1.data-item
												.dash-loader(v-if="loading.impressions")
													.loader-icon
														i.fas.fa-circle-notch.fa-spin
												Chart(
													:data-source="impressions",
													type="bar",
													argumentField="date",
													:size="{ height: 200 }",
													:bar-padding="0.75",
													:series="impressionsSeries"
												)
													template(v-slot:tooltip)
														DxTooltip(
															:enabled="true"
															:customize-tooltip="(point) => ({html: `${point.seriesName}: ${valueToLocale(point.value)} <br> Date: ${point.argument}`})"
														)
													template(v-slot:axis)
														DxValueAxis(
															:show-zero="true"
															:value-margins-enabled="false"
															name="amount"
															position="left"
														)
															DxLabel(:customize-text="(prop) => valueToLocale(prop.value)")

										.flex-row.space
											.flex-col.justify-center.align-center.flex-1.data-item
												.dash-loader(v-if="loading.top3Os")
													.loader-icon
														i.fas.fa-circle-notch.fa-spin
												ChartDoughnut(
													:data-source="top3Os"
													:max-value="`${getMaxValue(top3Os, 'value', '%')}`"
												)
												.label.upper {{$tCaption('Top 3 OS Types')}}

											.flex-col.justify-center.align-center.flex-1.data-item
												.dash-loader(v-if="loading.top5Ip")
													.loader-icon
														i.fas.fa-circle-notch.fa-spin
												ChartDoughnut(
													:data-source="top5Ip"
													:max-value="`${getMaxValue(top5Ip, 'value', '%')}`"
												)
												.label.upper {{$tCaption('Top 5 by IP')}}


											.flex-col.justify-center.align-center.flex-1.data-item
												.dash-loader(v-if="loading.top3Device")
													.loader-icon
														i.fas.fa-circle-notch.fa-spin
												ChartDoughnut(
													:data-source="top3Device"
													:max-value="`${getMaxValue(top3Device, 'value', '%')}`"
												)
												.label.upper {{$tCaption('Top 3 Devices Types')}}

											.flex-col.justify-center.align-center.flex-1.data-item
												.dash-loader(v-if="loading.top5Ua")
													.loader-icon
														i.fas.fa-circle-notch.fa-spin
												ChartDoughnut(
													:data-source="top5Ua"
													:max-value="`${getMaxValue(top5Ua, 'value', '%')}`"
												)
												.label.upper {{$tCaption('Top 5 by User Agent')}}

								.flex-row.space
									.flex-row.flex-3.space
										.flex-col.space-between.align-center.data-item
											.flex-col.flex-1.justify-center.align-center
												.dash-loader(v-if="loading.statistics")
													.loader-icon
														i.fas.fa-circle-notch.fa-spin
												ChartDoughnut(
													:data-source="[statisticsImpressions]",
													:max-value="statisticsImpressionsValue",
													:inner-radius="0.8",
													:size="{ width: 140, height: 140 }",
													:palette="['#a8a8a8']",
													:is-label="false"
												)
													template(v-slot:tooltip)
														DxTooltip(
															:enabled="true"
															:customize-tooltip="(point) => ({ html: `${point.argument}: ${valueToLocale(point.value)}` })"
														)
											.label.upper {{$tCaption('Impressions')}}

										.flex-col.justify-center.space
											.flex-col.justify-center.align-center.flex-1.data-item
												.dash-loader(v-if="loading.statistics")
													.loader-icon
														i.fas.fa-circle-notch.fa-spin
												.flex-col.justify-center.align-center.value.large {{ statisticsLtrValue }}
												.label {{$tCaption('LTR')}}

											.flex-col.justify-center.align-center.flex-1.data-item
												.dash-loader(v-if="loading.statistics")
													.loader-icon
														i.fas.fa-circle-notch.fa-spin
												.flex-col.justify-center.align-center.value.large {{ statisticsCtrValue }}
												.label {{$tCaption('CTR')}}

										.flex-col.flex-1.space-between.data-item
											.dash-loader(v-if="loading.statistics")
												.loader-icon
													i.fas.fa-circle-notch.fa-spin
											.flex-row.align-center.space-between
												.label {{$tCaption('Percentage Complete')}}
												.value {{ statisticsPercentageValue }}
											.flex-row.align-center.space-between
												.label {{$tCaption('Daily Target')}}
												.value {{ statisticsTargetValue }}
											.flex-row.align-center.space-between
												.label {{$tCaption('Avg Daily')}}
												.value {{ statisticsAvgValue }}
											.flex-row.align-center.space-between
												.label {{$tCaption('Est Complete')}}
												.value {{ statisticsEstValue }}
									.flex-col.flex-5.space-between.align-center.data-item
										.dash-loader(v-if="loading.creatives")
											.loader-icon
												i.fas.fa-circle-notch.fa-spin
										Chart(
											:data-source="creatives",
											type="spline",
											argumentField="name",
											:size="{ height: 200 }",
											:series="[{ value:'value', name:'Listen Through Ratio', axis: 'percentage' }]"
										)
											template(v-slot:tooltip)
												DxTooltip(
													:enabled="true"
													:shared="true"
													:customize-tooltip="(point) => ({html: `${formatNumber(point.value)}`})"
												)
											template(v-slot:axis)
												DxValueAxis(
													:show-zero="true"
													:value-margins-enabled="false"
													name="percentage"
													position="left"
												)
													DxLabel(:customize-text="({ valueText }) => `${valueText}`")
</template>

<script>
	import Chart from './components/Chart';
	import RangeSelector from './components/RangeSelector';
	import ChartDoughnut from './components/ChartDoughnut';
	import { LMap, LMarker, LTileLayer } from 'vue2-leaflet';
	import Vue2LeafletMarkerCluster from 'vue2-leaflet-markercluster';
	import DxButton from 'devextreme-vue/button';
	import DxCheckBox from 'devextreme-vue/check-box';
	import DxNumberBox from 'devextreme-vue/number-box';
	import DxRadioGroup from 'devextreme-vue/radio-group';
	import { DxLabel, DxTooltip, DxValueAxis, } from 'devextreme-vue/chart';
	import DxSelectBox from 'devextreme-vue/select-box';
	import DxDateBox from 'devextreme-vue/date-box';
	import moment from 'moment';
	import VueTypes from 'vue-types';
	import Loader from 'app-core/Loader.vue';
	import Dropdown from 'app-core/Dropdown.vue';

	import _ from "lodash";
	import Data from 'app-core/Data';

	export default {
		translationCategory: 'logs.Dashboard',
		name: "LogsDashboard",
		components: {
			LMap,
			LTileLayer,
			LMarker,
			'VMarkerCluster': Vue2LeafletMarkerCluster,
			Dropdown,
			RangeSelector,
			Chart,
			ChartDoughnut,
			DxCheckBox,
			DxRadioGroup,
			DxButton,
			DxNumberBox,
			DxSelectBox,
			DxDateBox,
			DxValueAxis,
			DxLabel,
			DxTooltip,
			Loader
		},
		props: {
			campaignId: VueTypes.any.def(null)
		},
		data() {
			const today = new Date();
			return {
				isDataLoaded: false,
				isLoading: false,
				campaigns: [],
				selectedCampaignId: this.campaignId,
				selectedCampaign: null,
				impressionsSeries: [{ value: 'impressions', name: 'Impressions' }, { value: 'target', name: 'Target' }],
				dateFilterStartValue: null,
				dateFilterEndValue: null,
				dateStartDropDownOptions: {
					toolbarItems: [
						{
							widget: 'dxButton',
							toolbar: 'bottom',
							location: 'center',
							options: {
								text: () => this.$tCaption('Today'),
								onClick: (e) => {
									if (__DEBUG__) console.log('click', e);
									this.$refs.dateStartDateBox.instance.option('value', new Date());
									this.$refs.dateStartDateBox.instance.close();
								}
							}
						}
					]
				},
				dateEndDropDownOptions: {
					toolbarItems: [
						{
							widget: 'dxButton',
							toolbar: 'bottom',
							location: 'center',
							options: {
								text: () => this.$tCaption('Today'),
								onClick: (e) => {
									if (__DEBUG__) console.log('click', e);
									this.$refs.dateEndDateBox.instance.option('value', new Date());
									this.$refs.dateEndDateBox.instance.close();
								}
							}
						}
					]
				},
				dropDownOptions: {
					minWidth: '500px'
				},
				filters: [
					{
						id: 'campaignPeriod',
						text: 'Campaign period',
						dateStart: null,
						dateEnd: null
					},
					{
						id: 'lastYear',
						text: 'Last Year',
						dateStart: `${today.getFullYear() - 1}-01-01`,
						dateEnd: `${today.getFullYear() - 1}-12-31`
					},
					{
						id: 'thisYear',
						text: 'This Year',
						dateStart: `${today.getFullYear()}-01-01`,
						dateEnd: this.formatDate(today, 'YYYY-MM-DD')
					},
					{
						id: 'lastMonth',
						text: 'Last Month',
						dateStart: this.$moment().startOf('month').add('days', -1).startOf('month').format('YYYY-MM-DD'),
						dateEnd: this.$moment().startOf('month').add('days', -1).endOf('month').format('YYYY-MM-DD')
					},
					{
						id: 'thisMonth',
						text: 'This Month',
						dateStart: this.$moment().startOf('month').format('YYYY-MM-DD'),
						dateEnd: this.$moment().endOf('month').format('YYYY-MM-DD')
					},
					{
						id: 'lastWeek',
						text: 'Last 7 Days',
						dateEnd: this.formatDate(today, 'YYYY-MM-DD'),
						dateStart: this.formatDate(today.setDate(today.getDate() - 6), 'YYYY-MM-DD')
					},
				],
				currentFilter: 'campaignPeriod',
				creativeIds: [],
				creativeColumns: [
					{ dataField: 'Name', caption: this.$tCaption('Creative'), width: 200 }, 
					{ dataField: 'Created', caption: this.$tCaption('Created'), dataType: 'date' },
					{ dataField: 'LastOnAir', caption: this.$tCaption('Last on air'), dataType: 'date' }, 
				],
				targetIds: [],
				targetColumns: [
					{ dataField: 'Name', caption: this.$tCaption('Target') }
				],
				baseMapUrl: 'https://{s}.basemaps.cartocdn.com/rastertiles/light_all/{z}/{x}/{y}@2x.png',
				attribution: '&copy; <a target="_blank" href="http://osm.org/copyright">OpenStreetMap</a> contributors',
				zoom: 8,
				map: null,
				mapObject: null,
				mapCenter: null,
				markerMaxValue: null,
				markerCluster: null,
				markerClusterOptions: {
					singleMarkerMode: true,
					spiderfyOnMaxZoom: false,
					showCoverageOnHover: false,
					zoomToBoundsOnClick: true,
					disableClusteringAtZoom: 18,
					iconCreateFunction: (cluster) => {
						let icon = {};

						if (this.markers.length) {
							const clusters = [...cluster.getAllChildMarkers()];
							const markers = clusters.map((c) => this.markers.find((m) => c._latlng.lat === m.lat && c._latlng.lng === m.lng));

							if (markers.length) {
								const totalValue = _.sumBy(markers, v => v.value) || null;
								const size = Math.min(80, Math.max(40, 100 * totalValue / this.markerMaxValue));

								icon = {
									html: `<b>${Data.formatNumber(totalValue, 'fixedPoint')}</b>`,
									iconSize: this.L.point(size, size),
									className: 'leaflet-marker-icon marker-cluster cluster-marker fade-in'
								};
							}
						}
						return this.L.divIcon(icon);
					}
				},

				rangeDataSource: null,
				rangeValue: [],
				rangeStartValue: this.$moment().startOf('month').toDate(),
				rangeEndValue: this.$moment().endOf('month').toDate(),
				rangeSeries: [
					{
						field: 'date',
						value: 'impressions',
					},
					{
						field: 'date',
						value: 'target',
					},
				],

				loading: {
					impressions: true,
					top3Os: true,
					top3Device: true,
					top5Ip: true,
					top5Ua: true,
					mapValues: true,
					creatives: true,
					creativeVariants: true,
					statistics: true
				},
				logs: {
					impressions: [],
					top3Os: [],
					top3Device: [],
					top5Ip: [],
					top5Ua: [],
					mapValues: [],
					creatives: [],
					creativeVariants: [],
					statistics: []
				},
				logsOptions: {
					campaignId: this.campaignId,
					dateStart: null,
					dateEnd: null,
					tzOffset: null,
				},
				selectedLanguage: null,
			};
		},
		computed: {
			L() {
				return window.L;
			},
			mapValues() {
				return 'mapValues' in this.logs
					? [...this.logs.mapValues].sort((a, b) => new Date(a.value) - new Date(b.value))
					: [];
			},
			markers() {
				return [...this.mapValues].map((c) => ({
					lat: c.latitude,
					lng: c.longitude,
					value: c.value
				}));
			},
			impressions() {
				return 'impressions' in this.logs
					? [...this.logs.impressions]
						.sort((a, b) => new Date(a.date) - new Date(b.date))
						.map((item) => ({
							...item,
							date: this.formatDate(item.date, 'YYYY-MM-DD')
						}))
					: [];
			},
			creatives() {
				return 'creatives' in this.logs
					? [...this.logs.creatives]
						.filter((l) => l.name !== null && l.value !== null)
						.map((l) => ({
							name: l.name,
							value: l.value / this.creativesTotal * 100 || 0,
						}))
					: [];
			},
			creativesTotal() {
				return _.sum(this.logs.creatives.map(f => f.value)) || 0;
			},
			creativeVariants() {
				return 'creativeVariants' in this.logs
					? this.logs.creatives
					: [];
			},
			top3Os() {
				return 'top3Os' in this.logs
					? [...this.logs.top3Os]
						.filter((l) => l.name !== null && l.value !== null)
						.map((l) => ({ ...l, value: this.formatNumber(l.value) }))
					: [];
			},
			top5Ip() {
				return 'top5Ip' in this.logs
					? [...this.logs.top5Ip]
						.filter((l) => l.name !== null && l.value !== null)
						.map((l) => ({ ...l, value: this.formatNumber(l.value) }))
					: [];
			},
			top3Device() {
				return 'top3Device' in this.logs
					? [...this.logs.top3Device]
						.filter((l) => l.name !== null && l.value !== null)
						.map((l) => ({ ...l, value: this.formatNumber(l.value) }))
					: [];
			},
			top5Ua() {
				return 'top5Ua' in this.logs
					? [...this.logs.top5Ua]
						.filter((l) => l.name !== null && l.value !== null)
						.map((l) => ({ ...l, value: this.formatNumber(l.value) }))
					: [];
			},
			// ------- statistics -------
			statistics() {
				return 'statistics' in this.logs
					? [...this.logs.statistics]
						.filter((l) => l.name !== null && l.value !== null)
						.map((l) => ({ ...l, value: l.value }))
					: [];
			},
			statisticsImpressions() {
				return this.getStatisticItem('Impressions') || {};
			},
			statisticsImpressionsValue() {
				let i = this.getStatisticItemValue('Impressions') || 0;
				return this.valueToLocale(i);
			},
			statisticsLtrValue() {
				return `${this.getStatisticItemValue('LTR') || 0}%`;
			},
			statisticsCtrValue() {
				return `${this.getStatisticItemValue('CTR') || 0}%`;
			},
			statisticsPercentageValue() {
				return `${this.getStatisticItemValue('PercentageComplete') || 0}%`;
			},
			statisticsTargetValue() {
				return `${this.reductionNumber(this.getStatisticItemValue('TargetDaily') || 0)}`;
			},
			statisticsAvgValue() {
				return `${this.reductionNumber(this.getStatisticItemValue('AvgDaily') || 0)}`;
			},
			statisticsEstValue() {
				return `${this.reductionNumber(this.getStatisticItemValue('EstCompletion') || 0)}`;
			}
		},
		mounted() {
			if (__DEBUG__)
				window.logsDashboard = this;
			this.selectedLanguage = this.$locale.state.currentLocale.code;
			if (this.campaignId) {
				this.selectCampaign(this.campaignId);
			}

			this.$getLogsDate = _.debounce(() => this.getLogs('date'), 2000, {
				maxWait: 4000,
				leading: false,
				trailing: true
			});
		},
		destroyed() {
			delete this.$getLogsDate; 
		},
		watch: {
			'$locale.state.currentLocale.code'(v) {
				this.selectedLanguage = v;
			},
			creativeIds() {
				this.$getLogsDate();
			},
			targetIds() {
				this.$getLogsDate();
			}
		},
		methods: {
			onCreativesSelect(v) {
				if (__DEBUG__)
					console.debug('onCreativesSelect', v);
			},
			getCreatives(pageIndex, pageSize, opts) {
				if (__DEBUG__)
					console.debug('Get creatives', { pageIndex, pageSize, opts });
				const filter = [['IsTemplate', '<>', true]];
				if (opts?.filter) {
					if (filter.length) filter.push('and');
					filter.push(opts?.filter);
				}
				const campaignId = this.campaignId || this.logsOptions.campaignId;
				if (campaignId) {
					if (filter.length) filter.push('and');
					filter.push(['Campaigns.Oid', 'contains', [campaignId]]);
				}
				const filterString = filter?.length ? `&filterCriteria=${encodeURIComponent(JSON.stringify(filter))}` : '';
				return this.$Data.getExternalApi('Creative/GetAllCreatives', `sorting=[{ selector: 'LastOnAir', desc: true }, { selector: 'Created', desc: true }]&page=${pageIndex + 1}${filterString}`, pageSize);
			},
			async getTargets(pageIndex, pageSize, opts) {
				if (__DEBUG__)
					console.debug('Get targets', { pageIndex, pageSize, opts });
				if (!this.creativeIds?.length) return { Results: [] };

				const filterString = opts?.filter ? `&filterCriteria=${encodeURIComponent(JSON.stringify(opts?.filter))}` : '';
				return this.$Data.getExternalApi('Creative/GetTargetsByCreatives', `creativeIds=${this.creativeIds.join(',')}&page=${pageIndex + 1}${filterString}`, pageSize);
			},
			getSelectedCampaign(campaignId) {
				this.selectedCampaignId = campaignId;
				return this.$Data
					.getExternalApi('Campaign/GetCampaign', `campaignId=${campaignId}`)
					.then(r => {
						this.selectedCampaign =
							Object.assign(
								{},
								{ ...this.lowerCaseKeys(r) },
								{ campaignId: r.Oid },
							);
						delete this.selectedCampaign.oid;

						const campaignStartDate = this.selectedCampaign.startDate;
						const campaignEndDate = this.selectedCampaign.endDate;
						const today = new Date();

						Object.assign(
							this.logsOptions,
							{
								campaignId: this.selectedCampaign.campaignId,
								dateStart: this.formatDate(campaignStartDate, 'YYYY-MM-DD'),
								dateEnd: this.formatDate(campaignEndDate, 'YYYY-MM-DD'),
								tzOffset: today.getTimezoneOffset() / 60,
							},
						);

						this.filters.forEach((f) => {
							switch (f.id) {
								case 'campaignPeriod':
									f.dateStart = campaignStartDate;
									f.dateEnd = campaignEndDate;
									break;
							}
						});

						this.rangeValue = [];
						this.rangeDataSource = [];
						this.rangeStartValue = new Date(campaignStartDate);
						this.rangeEndValue = new Date(campaignEndDate);
					});
			},
			async getLogs(type) {
				let params = 'p=1';

				if (!(this.logsOptions.campaignId || this.creativeIds?.length)) {
					this.isDataLoaded = false;
					return;
				}

				if (this.creativeIds?.length) {
					params += `&creativeIds=${this.creativeIds.join(',')}`;
				}

				if (this.targetIds?.length) {
					params += `&targetIds=${this.targetIds.join(',')}`;
				}

				Object
					.keys(this.logsOptions)
					.forEach((key) => {
						if (this.logsOptions[key]) {
							if (!params) params = `${key}=${this.logsOptions[key]}`;
							else params += `&${key}=${this.logsOptions[key]}`;
						}
					});


				//if (!this.filters.dateS)
				this.rangeDataSource = [];
				this.setLoadingCharts(null, true);
				this.isDataLoaded = true;
				this.isLoading = true;
				this.logs = {
					impressions: [],
					top3Os: [],
					top3Device: [],
					top5Ip: [],
					top5Ua: [],
					mapValues: [],
					creatives: [],
					creativeVariants: [],
					statistics: []
				};
				this.getDataLogs(params)
					.then(() => {
						this.isLoading = false;
						setImmediate(() => {
							this.updateMap();
							if (this.impressions.length > 0 && type === 'date') {
								this.rangeDataSource = this.impressions
									.map((item) => ({
										...item,
										date: new Date(item.date)
									}));
								this.rangeValue = [this.rangeStartValue, this.rangeEndValue];
							}
						});
					})
					.finally(() => {
						this.isLoading = false;
						this.setLoadingCharts(null, false);
					});
			},
			getDataLogs(params) {
				return this.$Data
					.getExternalApi(`DashboardData/GetStreamClientLogsData`, params)
					.then((logs) => {
						_.assign(this.logs, this.lowerCaseKeys(logs));
					});
			},
			// ---
			setLogs(prop = null, val = []) {
				if (prop) {
					Object.assign(this.logs, { [prop]: val });
				} else {
					Object.keys(this.logs).forEach((key) => {
						Object.assign(this.logs, { [key]: val });
					});
				}
			},
			setLoadingCharts(prop = null, state = true) {
				if (prop) {
					Object.assign(this.loading, { [prop]: state });
				} else {
					Object.keys(this.loading).forEach((key) => {
						Object.assign(this.loading, { [key]: state });
					});
				}
			},
			// --- Select events
			selectCampaign(campaignId) {
				Object.assign(
					this.logsOptions,
					{
						campaignId,
					},
				);

				this.setLogs();

				this.getSelectedCampaign(campaignId)
					.then(() => {
						this.getLogs('date');
					});
			},
			selectDate(date, prop) {
				console.log(`${prop}: ${date}`);
				let dateFilterValue = `range${prop.toLowerCase().includes('start') ? 'Start' : 'End'}Value`;

				if (new Date(date).getTime() !== new Date(dateFilterValue).getTime()) {
					this[dateFilterValue] = new Date(
						date
							? date
							: this.formatDate(
								this.selectedCampaign[`${prop.toLowerCase().includes('start') ? 'start' : 'end'}Date`],
								'YYYY-MM-DD'
							)
					);

					this.rangeValue = [];
					this.rangeDataSource = [];

					Object.assign(
						this.logsOptions,
						{
							[prop]: this.formatDate(date, 'YYYY-MM-DD'),
						},
					);

					this.getLogs('date');
				}
			},
			changeDateRadioFilter(id) {
				const filter = this.filters.find((f) => f.id === id);

				if (filter && filter.dateStart && filter.dateEnd) {
					this.rangeStartValue = new Date(filter.dateStart);
					this.rangeEndValue = new Date(filter.dateEnd);

					this.rangeValue = [];
					this.rangeDataSource = [];

					Object.assign(
						this.logsOptions,
						{
							dateStart: filter.dateStart,
							dateEnd: filter.dateEnd,
						},
					);

					this.getLogs('date');
				}
			},
			selectRangeValue(range, reset = false) {
				if (__DEBUG__) console.log('selectRangeValue', range);
				if (range.length) {
					this.rangeValue = range;
					if (!reset) {
						Object.assign(
							this.logsOptions,
							{
								dateStart: this.formatDate(range[0], 'YYYY-MM-DD'),
								dateEnd: this.formatDate(range[1], 'YYYY-MM-DD'),
							},
						);

						this.getLogs('range');
					}
				}
			},
			// --- Map
			updateMap() {
				if (__DEBUG__) console.log('updateMap');
				this.map = this.$refs.map.mapObject;
				this.markerCluster = this.$refs.markerCluster.mapObject;
				if (this.markers.length) {
					this.markerMaxValue = _.maxBy(this.markers, v => parseFloat(v.value)).value;
					const mapCenter = this.markers.find((c) => c.value === this.markerMaxValue);
					this.map.setView([mapCenter.lat, mapCenter.lng], 8);
				}
			},
			clusterClick(evt) {
				if (__DEBUG__) console.log('clusterClick', evt);
			},
			markerClick(evt) {
				if (__DEBUG__) console.log('markerClick', evt);
				this.mapCenter = evt.latlng;
			},
			updateMapCenter() {
			},
			updateMapZoom() {
			},
			// --- Statistics
			getStatisticItem(name) {
				return this.statistics.find((s) => s.name === name);
			},
			getStatisticItemValue(name) {
				const item = this.getStatisticItem(name);
				return parseFloat(parseFloat(item && 'value' in item && item.value !== 'NaN' ? item.value : null).toFixed(2));
			},
			// --- Utils
			getMaxValue(arr, prop = 'value', label) {
				return arr.length > 0
					? `${this.formatNumber(Math.max.apply(Math, arr.map((o) => o[prop])))}${label}`
					: '-';
			},
			reductionNumber(value) {
				if (!value) return value;

				const valueStr = value.toString().toLowerCase();
				const kIndex = valueStr.indexOf('k');

				let kStr = kIndex < 0 ? '' : valueStr.slice(kIndex);
				let num = kIndex < 0 ? value : Number(valueStr.slice(0, kIndex));

				kStr = Math.abs(num) > 999 ? kStr + 'k' : kStr;
				num = Math.abs(num) > 999 ? (Math.abs(num) / 1000).toFixed(0) : Math.abs(num);

				if (Math.abs(num) > 999) this.reductionNumber(num + kStr);

				return num + kStr;
			},
			formatNumber(value, max = 2) {
				const formatter = new Intl.NumberFormat('en-US', { maximumFractionDigits: max });
				if (typeof value === 'string' || value instanceof String) {
					return value.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1 ');
				}
				return formatter.format(value);
			},
			lowerCaseKeys(obj) {
				if (typeof obj !== 'object' || obj === null) return obj;
				if (Array.isArray(obj)) return obj.map(o => this.lowerCaseKeys(o));

				return Object
					.keys(obj)
					.reduce((acc, key) => {
						acc[key[0].toLowerCase() + key.slice(1)] = this.lowerCaseKeys(obj[key]);
						return acc;
					}, {});
			},
			formatDate(date, format) {
				return date ? moment(date).format(format) : null;
			},
			valueToLocale(value) {
				return parseFloat(value).toLocaleString(this.selectedLanguage);
			},
		}
	}
</script>

<style lang='scss'>
	@import "main/themes/generated/variables.base.scss";
	@import "~leaflet.markercluster/dist/MarkerCluster.css";
	@import "~leaflet.markercluster/dist/MarkerCluster.Default.css";

	.dashboard {
		font-family: "Roboto", "Helvetica Neue", Helvetica, Arial, sans-serif;

		.label {
			color: #a8a8a8;
		}

		.sidebar {
			width: fit-content;
			max-width: 150px;
			flex: 1;
			padding: 10px;
			margin-right: 20px;

			&__title {
				font-size: 20px;
				line-height: 36px;
				color: #f27649;
			}

			.dx-field {
				background: #FFFFFF;
				padding: 4px;
				margin-bottom: 4px;
				border: 1px solid #f3f3f3;

				&:last-of-type {
					margin-bottom: 0;
				}
			}

			.dx-field-label {
				width: calc(100% - 2px);
				font-size: 14px;
			}

			.dx-field-value {
				width: 20px;

				.dx-checkbox {
					margin: 0;
				}
			}
		}

		.date-box-filter {
			.dx-field {
				margin: 0 10px 0 0;
			}

			.dx-field-label {
				width: auto !important;
			}

			.dx-field-value {
				width: auto !important;
			}
		}

		.date-radio-filter {
			display: flex;
			flex-direction: column;
			width: fit-content;
		}

		.campaigns-total-grid {
			position: absolute;
			top: 10px;
			left: 50px;
			z-index: 1000;
			opacity: 0.9;
		}

		.compact {
			tr.dx-row > td:not(.no-padding) {
				padding: 8px !important;
			}

			tr.dx-row > td.no-padding {
				padding: 0px !important;
			}

			&.dx-button {
				margin-top: -4px;
			}
		}

		.dash-loader {
			display: flex;
			align-items: center;
			justify-content: center;
			position: absolute;
			top: 0;
			left: 0;
			width: 100%;
			height: 100%;
			background: rgba(255, 255, 255, 0.5);
			z-index: 5;

			.loader-icon {
				font-size: 40px;
				color: #f27649;
			}
		}

		.map-values {
			display: block;
			width: 100%;
			height: 100%;
			border: 1px solid #f3f3f3;
			position: relative;

			.map-holder {
				width: 100%;
				height: 100%;
				position: relative;
				z-index: 1;

				.map {
					width: 100%;
					height: 100%;
				}

				> .proposal-buttons-group {
					position: absolute;
					left: 10px;
					bottom: 10px;
				}

				.cluster-marker {
					display: flex;
					align-items: center;
					justify-content: center;
					border-radius: 100px;
					background: #f27649ab;
					color: #ffebe4;
					border: 2px solid #f27649ab;
				}
			}

			.fade-in {
				-webkit-animation: fade-in 0.4s cubic-bezier(0.390, 0.575, 0.565, 1.000) both;
				animation: fade-in 0.4s cubic-bezier(0.390, 0.575, 0.565, 1.000) both;
			}

			@-webkit-keyframes fade-in {
				0% {
					opacity: 0;
				}
				100% {
					opacity: 1;
				}
			}
			@keyframes fade-in {
				0% {
					opacity: 0;
				}
				100% {
					opacity: 1;
				}
			}
		}

		.data-item {
			border: 1px solid #f3f3f3;
			border-radius: 3px;
			background: #FFFFFF;
			min-width: 140px;
			min-height: 100px;
			padding: 16px;
			position: relative;

			.value {
				color: #1eb2f5;
				font-size: 1.5em;

				&.large {
					font-size: 2.5em;
				}
			}

			.label {
				&:not(:last-child) {
					margin-right: 10px;
				}
			}
		}
	}
</style>
